import { IEnvConfig } from '../envConfig/envConfig.types';
import { IAppConfig } from './appConfig.types';
import {
  getAppEnv,
  getAppPackageVersion,
  getApiHost,
  getGraphQlEndpoint,
  isProd,
  isMock,
  isLocalhost,
  getTargetBg,
  getAppVersionName,
  getQuantumEnvName,
  getTdcsDeviceType,
  isTrueProd,
  getRestEndpoint,
  getRumEnv,
  getRumKeys,
} from './appConfig.utils';

import { FeatureToggleName } from '../featureToggles/featureToggles.types';
import { getLocalFeatureTogglesByEnv } from '../featureToggles/featureToggles.utils';

/**
 * Create Application Config Object
 * @param envConfig the pipeline generated env config file
 */
export const createAppConfig = (envConfig: IEnvConfig): IAppConfig => {
  const appEnv = getAppEnv(envConfig);
  const appVersionName = getAppVersionName(envConfig);
  console.debug(`App running as '${appEnv}' env.`);

  const piClientId = 'enterprise-cxdo';
  const appShortName = 'cxdo';
  const appFullVersion = getAppPackageVersion(envConfig);
  const { piHost, directHost, senetHost, directSSOHost } = getApiHost(appEnv);
  const auth = `${piHost}/auth/oauth/v2`;

  const useDirectHost =
    envConfig.use_direct_host === 'true' ||
    import.meta.env.VITE_APP_USE_DIRECT_HOST === 'true';

  const graphql = getGraphQlEndpoint(
    useDirectHost ? directHost : piHost,
    useDirectHost
  );

  const restEndpoint = getRestEndpoint(
    useDirectHost ? directHost : piHost,
    useDirectHost
  );

  // DataDog FeatureToggle can only be changed by local toggles since appConfigContext is above the FeatureTogglesContext
  const localToggles = getLocalFeatureTogglesByEnv(appEnv);
  const togglesMap: Map<FeatureToggleName, boolean> = new Map(
    localToggles.toggles.map((toggle) => [toggle.name, toggle.isEnabled])
  );
  const isDataDogRumToggledOn =
    togglesMap.get(FeatureToggleName.DataDogRumIntegration) ?? false;

  const isDevBranch =
    appVersionName.includes('feature-') ||
    appVersionName.includes('local') ||
    appVersionName.includes('bugfix-');

  /**
   * App config stores anything static that's not expected to change at runtime or per environment.
   * If you're wanting to change settings at runtime (or per environment), use TDCS Config service.
   *
   * WARNING: Do not store any secrets (such as private API keys)!
   * For more info, see https://chalk.charter.com/display/ERSPM/FE+Configs.
   */
  return {
    isProd: isProd(appEnv),
    isTrueProd: isTrueProd(),
    isMock: isMock(appEnv),
    isLocal: isLocalhost(),

    app: {
      shortName: appShortName,
      displayName: 'Ops Center',
      env: appEnv,
      version: appFullVersion,
      versionName: appVersionName,
    },

    api: {
      useDirectHost,
      auth,
      graphql,
      senetApp: senetHost,
      restEndpoint,
      sso: directSSOHost,
      piHost,
    },

    auth: {
      targetBg: getTargetBg(),
      loginUrl: '/login',
      piClientId,
      piEsso: {
        clientId: piClientId,
        redirectUri: window.location.origin,
      },
      autoLogout: {
        idleWarningMins: 25, // after 25 mins, show the Idle Prompt Warning modal in AutoLogout
        postIdleWarnMins: 5, // after another 5 mins of the above modal displayed and still no response, auto log them out
      },
    },

    // Access Google API library
    google: {
      siteKey: '6LdBxjsUAAAAAKmr7DTZn8fp_6ZhQ0n_bKI5iXnf',
    },

    analytics: {
      // Quantum Intake Ticket: https://jira.charter.com/browse/XGANALYTIC-44457
      quantum: {
        enabled: true,
        // https://prism.spectruminternal.com/documentation/helix/endpoints
        devEndpoint: '//collector.pi-charter.net/topics/sspp?bulk=true',
        prodEndpoint: '//collector.pi.spectrum.net/topics/sspp?bulk=true',

        // See MetaData Tab under https://prism.spectruminternal.com/EntOpsCenter@Web/quantum-resources/helix/requirements/decoder/2.40
        config: {
          domain: 'portals',
          /**
           * When ready to use Quantum, need to register your App with Quantum Team, they give you this AppName value
           * New App Registration Form: https://prism.spectrumtoolbox.com/documentation/helix
           */
          appName: 'EntOpsCenter',
          appVersion: appVersionName,
          packageVersion: appFullVersion,
          appEnvName: getQuantumEnvName(appEnv),
          startSession: 'EntOpsCenter_startSession',
          requirementsVersion: '2.305', // ensure requirementsVersion up-to-date by checking https://prism.spectruminternal.com/EntOpsCenter@Web/quantum-resources/helix/requirements/versions
        },
      },

      adobe: {
        enabled: true,
        devEndpoint:
          '//assets.adobedtm.com/d5e6a435fd0f/61d188005020/launch-c755e44844e3-development.min.js',
        prodEndpoint:
          '//assets.adobedtm.com/d5e6a435fd0f/61d188005020/launch-f898116ba08a.min.js',
      },
    },

    dataDog: {
      enabled: isDataDogRumToggledOn && !isDevBranch,
      site: 'datadoghq.com',
      service: `${appShortName}-ui`.toLowerCase(),
      env: getRumEnv(appEnv),
      ...getRumKeys(),
      sessionSampleRate: envConfig.dd_session_sample_rate || 100,
      traceSampleRate: envConfig.dd_session_sample_rate || 100,
    },

    /**
     * Access our TDCS Config Server. Disabled by default.
     * If want to use TDCS, see https://chalk.charter.com/display/SPECENT/FE+Configs#FEConfigs-TDCSConfigs
     */
    tdcs: {
      enabled: false, // TODO: If want to enable, need to Setup TDCS configs above
      apiKey: 'EPRIZE',
      host: piHost,
      config: {
        appVersion: '1.0.0',
        clientType: 'EPRIZE-PORTAL',
        deviceId: '123456789',
        osVersion: '1.0.0',
        deviceType: getTdcsDeviceType(appShortName, appEnv),
      },
      // Allows for local config files for local development, see repo readme.
      localConfig: import.meta.env.VITE_APP_TDCS_CONFIG || '',
    },
  };
};
